<template>
<div style="overflow-x:hidden;">

<!-- ═══ HERO CAROUSEL ═══ -->
<div class="container-fluid p-0">
  <div id="header-carousel" class="carousel slide carousel-fade" data-bs-ride="carousel">
    <div class="carousel-inner">
      <div
        v-for="(slide, i) in heroSlides"
        :key="i"
        class="carousel-item"
        :class="{ active: i === 0 }"
      >
        <img class="w-100" :src="slide.img" :alt="slide.title" style="object-fit:cover;height:100vh;" />
        <div class="nf-hero-overlay d-flex flex-column align-items-center justify-content-center">
          <div class="nf-hero-content text-center">
            <span class="nf-slide-label">Nexus Focus Consultancy</span>
            <h1 class="nf-slide-title">{{ slide.title }}</h1>
            <div class="nf-slide-btns d-flex flex-wrap justify-content-center gap-3 mt-4">
              <router-link to="/quote" class="btn btn-primary btn-lg rounded-pill px-5">Get a Quote</router-link>
              <router-link to="/contact" class="btn btn-secondary btn-lg rounded-pill px-5">Contact Us</router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
    <button class="carousel-control-prev" type="button" data-bs-target="#header-carousel" data-bs-slide="prev">
      <span class="carousel-control-prev-icon" aria-hidden="true"></span>
    </button>
    <button class="carousel-control-next" type="button" data-bs-target="#header-carousel" data-bs-slide="next">
      <span class="carousel-control-next-icon" aria-hidden="true"></span>
    </button>
  </div>
</div>
<!-- ═══ END HERO ═══ -->


<!-- ═══ ABOUT TEASER ═══ -->
<section class="py-6 px-3 px-lg-5 nf-section-lines">
  <div class="container-fluid">
    <div class="row g-5 align-items-center">

      <!-- Image stack -->
      <div class="col-12 col-lg-5 nf-reveal-left">
        <div class="nf-img-stack">
          <img :src="'/img/nexus logo.jpg'" class="nf-img-back img-fluid rounded-4 shadow-lg" alt="Nexus Logo" />
          <img :src="'/img/carousel-1.jpeg'" class="nf-img-front img-fluid rounded-4 shadow-lg" alt="Our Work" />
          <div class="nf-years-badge" v-if="yearsStat">
            <span class="nf-years-num">{{ yearsStat.numeric_value }}{{ yearsStat.suffix }}</span>
            <span class="nf-years-label">{{ yearsStat.label }}</span>
          </div>
        </div>
      </div>

      <!-- Text -->
      <div class="col-12 col-lg-7 nf-reveal-right">
        <span class="section-label">Who We Are</span>
        <h2 class="section-title mb-3">Where Strategy Meets<br><em class="text-primary fst-italic">Sustainability</em></h2>
        <hr class="section-divider left mb-4" />

        <p class="mb-3">
          At Nexus Focus Consultancy, we are driven by the understanding that people, nature, culture, and governance are deeply interconnected.
        </p>
        <p class="mb-3">
          Founded on the belief that sustainable development must honour both tradition and innovation, we harmonize environmental conservation with inclusive progress.
        </p>
        <p class="mb-4">
          As a dynamic Kenyan consultancy, we sit at the intersection of research, policy, education, and grassroots action — empowering communities and co-creating solutions that strengthen climate resilience, reduce disaster risks, and promote environmental justice.
        </p>

        <div class="d-flex flex-wrap gap-3">
          <router-link to="/about" class="btn btn-primary px-5 py-2 rounded-pill">Learn More</router-link>
          <router-link to="/quote" class="btn btn-outline-primary px-5 py-2 rounded-pill">Get a Quote</router-link>
        </div>
      </div>
    </div>
  </div>
</section>
<!-- ═══ END ABOUT ═══ -->


<!-- ═══ STATS BAND ═══ -->
<section class="nf-stats-band py-5" ref="statsBand">
  <div class="container">
    <div class="row g-0 text-center nf-stagger">
      <div class="col-6 col-md-3" v-for="(stat, i) in stats" :key="i">
        <div class="nf-stat" :class="i < stats.length - 1 ? 'border-end border-white border-opacity-20' : ''">
          <div class="nf-stat-icon" v-if="stat.icon"><i :class="stat.icon"></i></div>
          <div class="nf-stat-num">
            <span class="nf-counter">{{ stat.animated }}</span>{{ stat.suffix }}
          </div>
          <div class="nf-stat-label">{{ stat.label }}</div>
        </div>
      </div>
    </div>
  </div>
</section>
<!-- ═══ END STATS ═══ -->


<!-- ═══ CORE VALUES ═══ -->
<section class="py-6 px-3 px-lg-5 nf-section-dots">
  <div class="container-fluid">
    <div class="text-center mb-5 nf-reveal">
      <span class="section-label">Our Principles</span>
      <h2 class="section-title">Core Values</h2>
      <hr class="section-divider" />
    </div>
    <div class="row g-4 justify-content-center nf-stagger">
      <div class="col-12 col-sm-6 col-lg-4" v-for="(val, i) in coreValues" :key="i">
        <div class="nf-card h-100">
          <div class="nf-icon-wrap"><i :class="val.icon"></i></div>
          <h5 class="mb-2">{{ val.title }}</h5>
          <p class="mb-0 small">{{ val.description }}</p>
        </div>
      </div>
    </div>
  </div>
</section>
<!-- ═══ END VALUES ═══ -->


<!-- ═══ SERVICES PREVIEW ═══ -->
<section class="py-6 px-3 px-lg-5 nf-section-lines">
  <div class="container-fluid">
    <div class="text-center mb-5 nf-reveal">
      <span class="section-label">What We Do</span>
      <h2 class="section-title">Our Services</h2>
      <hr class="section-divider" />
      <p class="text-muted mx-auto" style="max-width:600px;">
        Innovative, impact-driven solutions across sustainability, engineering, and community development.
      </p>
    </div>
    <div class="row g-4 nf-stagger">
      <div class="col-12 col-sm-6 col-lg-4" v-for="(svc, i) in services" :key="i">
        <div class="nf-service-card h-100">
          <div class="nf-service-icon" :style="{ background: svc.bg }">
            <i :class="svc.icon" :style="{ color: svc.color }" style="font-size:1.5rem;"></i>
          </div>
          <h5 class="mb-2">{{ svc.title }}</h5>
          <p class="small text-muted mb-0">{{ svc.desc }}</p>
        </div>
      </div>
    </div>
    <div class="text-center mt-5 nf-reveal">
      <router-link to="/services" class="btn btn-primary px-5 py-2 rounded-pill">View All Services</router-link>
    </div>
  </div>
</section>
<!-- ═══ END SERVICES ═══ -->


<!-- ═══ PROJECTS PREVIEW ═══ -->
<section class="py-6 px-3 px-lg-5 nf-section-dots" v-if="featuredProjects.length">
  <div class="container-fluid">
    <div class="d-flex align-items-end justify-content-between flex-wrap gap-3 mb-5 nf-reveal">
      <div>
        <span class="section-label">Our Work</span>
        <h2 class="section-title mb-0">Featured Projects</h2>
      </div>
      <router-link to="/projects" class="btn btn-outline-primary rounded-pill px-4">
        View All Projects <i class="bi bi-arrow-right ms-1"></i>
      </router-link>
    </div>
    <div class="row g-4 nf-stagger">
      <div class="col-12 col-md-6 col-lg-4" v-for="proj in featuredProjects.slice(0,3)" :key="proj.id">
        <div class="nf-proj-prev-card">
          <div class="nf-pp-img">
            <img :src="proj.image_url || '/img/carousel-1.jpeg'" :alt="proj.title" />
            <div class="nf-pp-overlay"></div>
            <div v-if="proj.category" class="nf-pp-cat" :style="{background: proj.category.color}">
              <i :class="proj.category.icon_class + ' me-1'"></i>{{ proj.category.name }}
            </div>
          </div>
          <div class="nf-pp-body">
            <h5 class="nf-pp-title">{{ proj.title }}</h5>
            <p class="text-muted small mb-2">
              <i class="fas fa-briefcase me-1 text-primary"></i>{{ proj.client_name }}
              <span v-if="proj.year_completed" class="ms-3">
                <i class="fas fa-calendar-alt me-1 text-primary"></i>{{ proj.year_completed }}
              </span>
            </p>
            <p class="text-muted small mb-0" style="display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;">
              {{ proj.description }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>
<!-- ═══ END PROJECTS PREVIEW ═══ -->

<!-- ═══ CTA BAND ═══ -->
<section class="nf-cta-band py-6 px-3 text-center">
  <!-- Floating decorative circles -->
  <div class="nf-deco-circle" style="width:320px;height:320px;background:#fff;top:-100px;left:-80px;"></div>
  <div class="nf-deco-circle" style="width:200px;height:200px;background:var(--nf-gold);bottom:-60px;right:10%;"></div>
  <div class="nf-deco-circle" style="width:120px;height:120px;background:#fff;top:30%;right:-40px;"></div>
  <div class="container nf-reveal" style="max-width:680px;position:relative;z-index:1;">
    <span class="section-label" style="color:rgba(255,255,255,0.7);">Ready to Work Together?</span>
    <h2 class="text-white mt-1 mb-3" style="font-size:clamp(1.8rem,3vw,2.5rem);">
      Let's Build Resilient, Sustainable Solutions
    </h2>
    <p class="text-white mb-4" style="opacity:0.85;line-height:1.8;">
      Whether you represent a government body, development partner, or community organisation — we're ready to co-create impact with you.
    </p>
    <div class="d-flex flex-wrap justify-content-center gap-3">
      <router-link to="/quote" class="btn btn-primary btn-lg rounded-pill px-5">Request a Service</router-link>
      <router-link to="/contact" class="btn btn-secondary btn-lg rounded-pill px-5">Contact Us</router-link>
    </div>
  </div>
</section>
<!-- ═══ END CTA ═══ -->


<!-- ═══ BLOG PREVIEW ═══ -->
<section class="py-6 px-3 px-lg-5 nf-section-lines">
  <div class="container-fluid">
    <div class="text-center mb-5 nf-reveal">
      <span class="section-label">Knowledge Hub</span>
      <h2 class="section-title">Latest Insights</h2>
      <hr class="section-divider" />
    </div>

    <div v-if="loading" class="text-center py-5 text-muted">Loading posts…</div>
    <div v-else-if="!blogs.length" class="text-center py-5 text-muted">No posts yet.</div>
    <div v-else class="row g-4 nf-stagger">
      <div
        v-for="blog in blogs.slice(0,3)"
        :key="blog.id"
        class="col-12 col-md-6 col-lg-4"
      >
        <div class="nf-blog-card">
          <div class="card-img-wrap">
            <img :src="getImageUrl(blog.image)" :alt="blog.title" />
            <div class="nf-date-badge bg-primary text-white text-center">
              <div class="fw-bold fs-5 lh-1">{{ new Date(blog.date).getDate() }}</div>
              <div class="text-uppercase" style="font-size:.7rem;">{{ new Date(blog.date).toLocaleString('default',{month:'short'}) }}</div>
            </div>
          </div>
          <div class="p-4">
            <div class="d-flex gap-3 text-muted small mb-2">
              <span><i class="bi bi-person me-1"></i>{{ blog.author }}</span>
              <span><i class="bi bi-tag me-1"></i>{{ blog.categories?.[0]?.name || 'General' }}</span>
            </div>
            <router-link :to="`/blog/${blog.slug}`" class="h5 d-block text-dark text-decoration-none lh-sm mb-3 nf-blog-title">
              {{ blog.title }}
            </router-link>
            <p class="text-muted small mb-3" v-if="blog.excerpt">{{ blog.excerpt.slice(0,110) }}…</p>
            <router-link :to="`/blog/${blog.slug}`" class="btn btn-sm btn-outline-primary rounded-pill px-3">
              Read More <i class="bi bi-arrow-right ms-1"></i>
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <div class="text-center mt-5">
      <router-link to="/blog" class="btn btn-outline-primary px-5 py-2 rounded-pill">View All Posts</router-link>
    </div>
  </div>
</section>
<!-- ═══ END BLOG ═══ -->

</div>
</template>

<script>
export default {
  name: 'HomeView',
  data() {
    return {
      blogs: [],
      featuredProjects: [],
      loading: false,
      heroSlides: [
        { img: 'img/Consultation1.jpg', title: 'Where Strategy Meets Sustainability' },
        { img: 'img/Consultation2.jpg', title: 'Research, Assessments & Technical Studies' },
        { img: 'img/Consultation3.jpg', title: 'Capacity Building & Policy Engagement' },
        { img: 'img/Consultation4.jpg', title: 'Innovative Solutions for Sustainable Development' },
      ],
      stats: [],
      statsInView: false,
      coreValues: [
        { icon: 'fas fa-gavel',     title: 'Integrity',      description: 'Upholding transparency, accountability, and ethical practices in all our work.' },
        { icon: 'fas fa-users',     title: 'Inclusivity',    description: 'Fostering gender equity, youth participation, and community-driven development.' },
        { icon: 'fas fa-lightbulb', title: 'Innovation',     description: 'Leveraging creativity, science, and local knowledge to drive impactful solutions.' },
        { icon: 'fas fa-leaf',      title: 'Sustainability', description: 'Advancing long-term, resilient solutions that protect ecosystems and livelihoods.' },
        { icon: 'fas fa-handshake', title: 'Collaboration',  description: 'Building strong, cross-sector partnerships for meaningful and lasting change.' },
      ],
      services: [
        { icon: 'fa fa-search',        title: 'Research & Technical Studies',  desc: 'Vulnerability assessments, EIA, GIS mapping, and technical policy research.',  bg: '#e7f4ed', color: '#1a6b4a' },
        { icon: 'fa fa-leaf',          title: 'Climate & Resource Management', desc: 'Adaptation planning, biodiversity, renewable energy and carbon credits.',         bg: '#e8f8f0', color: '#1a7a4a' },
        { icon: 'fa fa-chart-line',    title: 'ESG & Sustainability',           desc: 'ESG risk analysis, sustainability reporting, and SDG integration.',              bg: '#fdf4e0', color: '#c9992d' },
        { icon: 'fa fa-tools',         title: 'Engineering & Project Support',  desc: 'Feasibility studies, construction oversight, and engineering support.',          bg: '#fce8e8', color: '#c0392b' },
        { icon: 'fa fa-users',         title: 'Community Development & DRR',   desc: 'Livelihoods, DRR, gender inclusion, and access to essential services.',         bg: '#e8f0fc', color: '#2563eb' },
        { icon: 'fa fa-graduation-cap',title: 'Education & Capacity Building', desc: 'Training, awareness, and preparedness for institutions and communities.',        bg: '#f4e7fd', color: '#7c3aed' },
      ],
    };
  },
  computed: {
    yearsStat() {
      return this.stats.find(s => /year/i.test(s.label)) || null;
    },
  },
  mounted() {
    this.fetchBlogs();
    this.fetchStats();
    this.fetchFeaturedProjects();
    this.initCounterObserver();
  },
  beforeUnmount() {
    if (this._statsObserver) this._statsObserver.disconnect();
  },
  methods: {
    async fetchStats() {
      try {
        const res  = await fetch('https://admin.nexusfocusconsultancy.com/stats/api/stats/');
        const data = await res.json();
        if (Array.isArray(data) && data.length) {
          this.stats = data.map(s => ({
            numeric_value: s.numeric_value,
            suffix:        s.suffix,
            label:         s.label,
            animated:      0,
          }));
          // If user already scrolled past the stats band, animate immediately
          if (this.statsInView) this.animateCounters();
        }
      } catch (e) {
        console.error('Failed to fetch stats:', e);
      }
    },
    initCounterObserver() {
      this._statsObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.statsInView = true;
            if (this.stats.length) this.animateCounters();
            this._statsObserver.disconnect();
          }
        });
      }, { threshold: 0.4 });
      this.$nextTick(() => {
        if (this.$refs.statsBand) this._statsObserver.observe(this.$refs.statsBand);
      });
    },
    animateCounters() {
      this.stats.forEach((stat, idx) => {
        const target   = stat.numeric_value;
        const duration = 1800;
        const step     = 16;
        const increment = target / (duration / step);
        let current = 0;
        const timer = setInterval(() => {
          current += increment;
          if (current >= target) {
            current = target;
            clearInterval(timer);
          }
          this.stats[idx].animated = Math.floor(current);
        }, step);
      });
    },
    async fetchFeaturedProjects() {
      try {
        const res = await fetch('https://admin.nexusfocusconsultancy.com/projects/api/projects/?featured=true');
        this.featuredProjects = await res.json();
        this.$nextTick(() => window.dispatchEvent(new Event('nf-reobserve')));
      } catch (e) {
        console.error('Failed to fetch featured projects:', e);
      }
    },
    async fetchBlogs() {
      this.loading = true;
      try {
        const res = await fetch('https://admin.nexusfocusconsultancy.com/blog/api/posts/');
        const data = await res.json();
        this.blogs = data.results || data || [];
        this.$nextTick(() => window.dispatchEvent(new Event('nf-reobserve')));
      } catch (e) {
        console.error('Failed to fetch blogs:', e);
      } finally {
        this.loading = false;
      }
    },
    getImageUrl(image) {
      if (!image) return '/img/carousel-1.jpeg';
      if (image.startsWith('http')) return image;
      return `https://media.nexusfocusconsultancy.com${image}`;
    },
  },
};
</script>

<style scoped>
/* ── Hero ── */
.nf-hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(14,69,48,0.82) 0%, rgba(17,28,34,0.6) 100%);
}
.nf-hero-content { max-width: 820px; padding: 0 1rem; }
.nf-slide-label {
  display: block;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.75);
  margin-bottom: 0.75rem;
}
.nf-slide-title {
  font-family: 'Playfair Display', serif;
  color: #fff;
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 700 !important;
  line-height: 1.2;
  text-shadow: 0 2px 24px rgba(0,0,0,0.3);
}

/* ── About image stack ── */
.nf-img-stack {
  position: relative;
  padding-bottom: 60px;
  padding-right: 60px;
}
.nf-img-back {
  width: 100%;
  object-fit: cover;
}
.nf-img-front {
  position: absolute;
  bottom: 0; right: 0;
  width: 55%;
  border: 5px solid #fff;
  box-shadow: 0 12px 40px rgba(0,0,0,0.18);
}
.nf-years-badge {
  position: absolute;
  top: 24px; right: 24px;
  background: var(--nf-gold, #c9992d);
  color: #fff;
  border-radius: 50%;
  width: 90px; height: 90px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  box-shadow: 0 6px 20px rgba(201,153,45,0.45);
  line-height: 1.2;
}
.nf-years-num { font-family:'Playfair Display',serif; font-size:1.5rem; font-weight:700; }
.nf-years-label { font-size: 0.62rem; font-weight:600; text-transform:uppercase; letter-spacing:0.05em; opacity:0.9; }

/* ── Stats band ── */
.nf-stats-band {
  background: linear-gradient(135deg, var(--nf-green-dark, #0e4530) 0%, var(--nf-green, #1a6b4a) 100%);
}
.nf-stats-band .nf-stat-num  { color: #fff; font-size: 2.5rem; }
.nf-stats-band .nf-stat-label { color: rgba(255,255,255,0.75); }

/* ── CTA band ── */
.nf-cta-band {
  background: linear-gradient(135deg, var(--nf-charcoal, #2c3e50) 0%, var(--nf-green-dark, #0e4530) 100%);
  position: relative;
  overflow: hidden;
}
.nf-cta-band::before {
  content: '';
  position: absolute; inset: 0;
  background-image:
    linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px);
  background-size: 40px 40px;
}

/* ── Blog card title hover ── */
.nf-blog-title:hover { color: var(--nf-green, #1a6b4a) !important; }

/* ── Date badge on blog cards ── */
.nf-date-badge {
  position: absolute;
  top: 12px; left: 12px;
  padding: 0.4rem 0.6rem;
  border-radius: 8px;
  line-height: 1.3;
  box-shadow: 0 3px 10px rgba(0,0,0,0.25);
}

/* ── Project preview cards ── */
.nf-proj-prev-card {
  background: #fff;
  border-radius: 14px;
  border: 1px solid var(--nf-border, #dce8e0);
  overflow: hidden;
  transition: transform .3s, box-shadow .3s;
}
.nf-proj-prev-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 20px 50px rgba(26,107,74,.12);
}
.nf-pp-img {
  position: relative; height: 200px; overflow: hidden;
}
.nf-pp-img img { width:100%; height:100%; object-fit:cover; transition: transform .5s; }
.nf-proj-prev-card:hover .nf-pp-img img { transform: scale(1.07); }
.nf-pp-overlay {
  position:absolute; inset:0;
  background: linear-gradient(to top, rgba(0,0,0,.45) 0%, transparent 55%);
}
.nf-pp-cat {
  position:absolute; bottom:10px; left:10px;
  color:#fff; font-size:.68rem; font-weight:700;
  padding:.22rem .65rem; border-radius:20px;
  display:flex; align-items:center;
}
.nf-pp-body { padding: 1.25rem 1.4rem; }
.nf-pp-title {
  font-family: 'Playfair Display', serif;
  font-size: 1rem; line-height:1.35; margin-bottom:.5rem;
  color: var(--nf-dark,#111c22);
}

@media (max-width: 767px) {
  .nf-img-stack { padding-right: 40px; padding-bottom: 40px; }
  .nf-img-front { width: 50%; }
  .nf-years-badge { width: 72px; height: 72px; }
  .nf-years-num { font-size: 1.2rem; }
}
</style>
